/**
 * Skills v1 M11 item 70 — CL Observability tab.
 *
 * Renders the CL loop's queue health + throughput + coverage using the
 * `/api/skills/observability` endpoint. Tenant-scoped by JWT; no external
 * dashboard or chart library — all rendering is inline SVG + CSS.
 *
 * Sections (top to bottom):
 *   1. 4 headline metric cards
 *   2. Queue depth bar chart (source_channel × status)
 *   3. Applied vs Rejected daily line chart (SVG polyline)
 *   4. Per-agent table (correction volume by agent)
 *   5. Per-skill table (correction volume by base skill)
 *   6. Recent applications table (last 20)
 */
import { useEffect, useMemo, useState } from "react";
import { skillsApi } from "../../api/client";
import { COLORS } from "../../theme/colors";
import { MATERIALS, RADIUS } from "../../theme/materials";

// Theme-aware tokens (dark/light adaptivity). Legacy hex fallbacks preserved
// for envs where COLORS.* keys are unset. Palette matches parent SkillsView.
const RED = COLORS.red || "#ef4444";
const GREEN = COLORS.green || "#059669";
const AMBER = COLORS.orange || "#d97706";
const BLUE = COLORS.accentText || COLORS.accent || "#3b82f6";
const VIOLET = COLORS.purple || "#7c3aed";
const SLATE = COLORS.textMuted || "#64748b";
const HEADING = COLORS.text || "#0f172a";
const HEADING_ALT = COLORS.textDim || "#475569";
const SUBTLE_BG = COLORS.surfaceHover || "#f1f5f9";
const SURFACE = COLORS.surface || "#fff";
const BORDER = COLORS.border || "#e2e8f0";
const CHIP_BG = COLORS.surfaceHover || "#fbfcfd";
const CHIP_BORDER = COLORS.border || "#e2e8f0";

const CHANNEL_COLOR = {
  eval: BLUE,
  trickle_in_prod: GREEN,
  trickle_in_dev: VIOLET,
  backfill: AMBER,
  manual: SLATE,
};

const STATUS_COLOR = {
  pending: AMBER,
  applied: GREEN,
  rejected: RED,
  superseded: SLATE,
};

// Card surface — uses the app's Glassmorphism 2.0 thick-glass material
// (v13.1 visual identity). Function form (not static object) because
// MATERIALS.thickGlass reads getThemeMode() at call time, so a single
// component re-renders correctly across dark/light theme toggle.
const _card = () => ({
  ...MATERIALS.thickGlass(),
  padding: "14px 18px",
  borderRadius: RADIUS.inner,
});

const _sectionHead = {
  fontSize: 12,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  fontWeight: 600,
  color: HEADING_ALT,
  margin: "24px 0 12px",
};

// ─── 1. Headline metric card ─────────────────────────────────────────

function MetricCard({ label, value, unit }) {
  return (
    <div style={{ ..._card(), flex: 1, minWidth: 180 }}>
      <div style={{ fontSize: 11, color: SLATE, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600 }}>
        {label}
      </div>
      <div style={{ marginTop: 6, fontSize: 26, fontWeight: 700, color: HEADING }}>
        {typeof value === "number" ? value.toLocaleString() : value}
        {unit ? <span style={{ fontSize: 12, color: SLATE, marginLeft: 6, fontWeight: 400 }}>{unit}</span> : null}
      </div>
    </div>
  );
}

// ─── 2. Queue depth bar chart ─────────────────────────────────────────

function QueueBars({ rows }) {
  // Group rows by source_channel → sum + per-status breakdown.
  const grouped = useMemo(() => {
    const g = {};
    for (const r of rows || []) {
      const k = r.source_channel;
      if (!g[k]) g[k] = { total: 0, statuses: {} };
      g[k].total += r.count;
      g[k].statuses[r.status] = (g[k].statuses[r.status] || 0) + r.count;
    }
    return g;
  }, [rows]);
  const channels = Object.keys(grouped).sort((a, b) => grouped[b].total - grouped[a].total);
  const max = Math.max(1, ...channels.map((c) => grouped[c].total));

  if (channels.length === 0) {
    return (
      <div style={{ ..._card(), color: SLATE, fontSize: 13 }}>
        No pending examples yet. Once an eval runs (M12 auto-trigger) or a live fact-check verdict fires (trickle-in), rows will appear here.
      </div>
    );
  }

  return (
    <div style={{ ..._card(), padding: 20 }}>
      {channels.map((ch) => {
        const g = grouped[ch];
        const statusKeys = Object.keys(g.statuses);
        const pctOfMax = (g.total / max) * 100;
        return (
          <div key={ch} style={{ marginBottom: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4, color: HEADING_ALT }}>
              <span>
                <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: CHANNEL_COLOR[ch] || SLATE, marginRight: 6 }} />
                <strong>{ch}</strong>
              </span>
              <span style={{ color: SLATE }}>{g.total} total</span>
            </div>
            <div style={{ display: "flex", height: 22, borderRadius: 4, overflow: "hidden", background: SUBTLE_BG, width: `${Math.max(4, pctOfMax)}%` }}>
              {statusKeys.map((s) => (
                <div key={s}
                  title={`${s}: ${g.statuses[s]}`}
                  style={{ background: STATUS_COLOR[s] || SLATE, width: `${(g.statuses[s] / g.total) * 100}%` }} />
              ))}
            </div>
          </div>
        );
      })}
      {/* Legend */}
      <div style={{ marginTop: 16, display: "flex", gap: 14, fontSize: 11, color: SLATE, flexWrap: "wrap" }}>
        {Object.entries(STATUS_COLOR).map(([s, c]) => (
          <span key={s}>
            <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: c, marginRight: 4, verticalAlign: "middle" }} />
            {s}
          </span>
        ))}
      </div>
    </div>
  );
}

// ─── 3. Daily line chart (SVG polyline) ──────────────────────────────

function DailyLineChart({ series }) {
  const points = series || [];
  if (points.length === 0) {
    return (
      <div style={{ ..._card(), color: SLATE, fontSize: 13 }}>
        No history yet. Applied + rejected counts will populate per day as CL runs.
      </div>
    );
  }
  const width = 640;
  const height = 180;
  const pad = 32;
  const maxVal = Math.max(1, ...points.map((p) => Math.max(p.applied, p.rejected)));
  const step = points.length > 1 ? (width - 2 * pad) / (points.length - 1) : 0;

  const toXY = (v, i) => [pad + i * step, height - pad - (v / maxVal) * (height - 2 * pad)];
  const appliedPath = points.map((p, i) => toXY(p.applied, i)).map(([x, y]) => `${x},${y}`).join(" ");
  const rejectedPath = points.map((p, i) => toXY(p.rejected, i)).map(([x, y]) => `${x},${y}`).join(" ");

  return (
    <div style={{ ..._card(), padding: 18 }}>
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: "100%", height: "auto", display: "block" }}>
        {/* Y-axis grid */}
        {[0, 0.25, 0.5, 0.75, 1].map((f) => {
          const y = height - pad - f * (height - 2 * pad);
          return (
            <g key={f}>
              <line x1={pad} x2={width - pad} y1={y} y2={y} stroke={BORDER} strokeWidth={1} />
              <text x={4} y={y + 4} fontSize={10} fill={SLATE}>{Math.round(f * maxVal)}</text>
            </g>
          );
        })}
        {/* Applied line */}
        <polyline points={appliedPath} fill="none" stroke={GREEN} strokeWidth={2} />
        {points.map((p, i) => {
          const [x, y] = toXY(p.applied, i);
          return <circle key={`a-${i}`} cx={x} cy={y} r={3} fill={GREEN} />;
        })}
        {/* Rejected line */}
        <polyline points={rejectedPath} fill="none" stroke={RED} strokeWidth={2} strokeDasharray="4 3" />
        {points.map((p, i) => {
          const [x, y] = toXY(p.rejected, i);
          return <circle key={`r-${i}`} cx={x} cy={y} r={2.5} fill={RED} />;
        })}
        {/* X-axis labels — first / last / middle only to avoid clutter */}
        {points.length > 0 ? (
          <>
            <text x={pad} y={height - 8} fontSize={9} fill={SLATE}>{points[0].date}</text>
            <text x={width - pad} y={height - 8} fontSize={9} fill={SLATE} textAnchor="end">{points[points.length - 1].date}</text>
          </>
        ) : null}
      </svg>
      <div style={{ display: "flex", gap: 16, fontSize: 12, color: SLATE, marginTop: 8 }}>
        <span><span style={{ display: "inline-block", width: 12, height: 2, background: GREEN, verticalAlign: "middle", marginRight: 6 }} />Applied</span>
        <span><span style={{ display: "inline-block", width: 12, height: 2, background: RED, verticalAlign: "middle", marginRight: 6 }} />Rejected (dashed)</span>
      </div>
    </div>
  );
}

// ─── 4/5. Sortable table ─────────────────────────────────────────────

function SortableTable({ rows, columns, empty }) {
  const [sortKey, setSortKey] = useState(columns[0]?.key);
  const [sortDesc, setSortDesc] = useState(true);
  const sorted = useMemo(() => {
    const arr = [...(rows || [])];
    arr.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av === bv) return 0;
      return sortDesc ? (av < bv ? 1 : -1) : (av < bv ? -1 : 1);
    });
    return arr;
  }, [rows, sortKey, sortDesc]);

  if (!rows || rows.length === 0) {
    return <div style={{ ..._card(), color: SLATE, fontSize: 13 }}>{empty}</div>;
  }

  return (
    <div style={{ ..._card(), padding: 0, overflow: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key}
                onClick={() => {
                  if (c.key === sortKey) setSortDesc(!sortDesc);
                  else { setSortKey(c.key); setSortDesc(true); }
                }}
                style={{
                  textAlign: c.numeric ? "right" : "left",
                  padding: "10px 14px",
                  borderBottom: `1px solid ${BORDER}`,
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  color: HEADING_ALT,
                  cursor: "pointer",
                  userSelect: "none",
                  whiteSpace: "nowrap",
                }}>
                {c.label} {sortKey === c.key ? (sortDesc ? "▼" : "▲") : ""}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r, i) => (
            <tr key={i} style={{ background: i % 2 === 1 ? SUBTLE_BG : "transparent" }}>
              {columns.map((c) => (
                <td key={c.key}
                  style={{
                    padding: "8px 14px",
                    textAlign: c.numeric ? "right" : "left",
                    borderBottom: `1px solid ${BORDER}`,
                    color: HEADING_ALT,
                    fontFamily: c.mono ? "SF Mono, Menlo, monospace" : undefined,
                    fontSize: c.mono ? 12 : 13,
                  }}>
                  {c.render ? c.render(r[c.key], r) : (r[c.key] ?? "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Main tab component ──────────────────────────────────────────────

export default function CLObservabilityTab() {
  const [data, setData] = useState(null);
  const [since, setSince] = useState("7d");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const load = async (window = since) => {
    setBusy(true);
    setErr(null);
    try {
      const rsp = await skillsApi.getObservability(window);
      setData(rsp);
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(since); }, [since]);

  if (!data && !err) {
    return <div style={{ padding: 20, color: SLATE }}>Loading observability…</div>;
  }
  if (err) {
    return <div style={{ padding: 20, color: RED }}>Failed to load: {err}</div>;
  }

  return (
    <div style={{ padding: "8px 4px" }}>
      {/* Header + window picker */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: HEADING }}>
          Continual Learning — Observability
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12 }}>
          <span style={{ color: SLATE }}>Window:</span>
          {["24h", "7d", "30d", "90d"].map((w) => (
            <button key={w} onClick={() => setSince(w)}
              style={{
                padding: "4px 10px",
                background: w === since ? (COLORS.accent || BLUE) : SURFACE,
                color: w === since ? "#fff" : HEADING_ALT,
                border: `1px solid ${w === since ? (COLORS.accent || BLUE) : BORDER}`,
                borderRadius: 4,
                cursor: "pointer",
                fontSize: 12,
                fontWeight: 500,
              }}>
              {w}
            </button>
          ))}
          <button onClick={() => load(since)} disabled={busy}
            style={{
              padding: "4px 10px",
              background: SURFACE,
              color: HEADING_ALT,
              border: `1px solid ${BORDER}`,
              borderRadius: 4,
              cursor: busy ? "wait" : "pointer",
              fontSize: 12,
              fontWeight: 500,
            }}>
            {busy ? "…" : "Refresh"}
          </button>
        </div>
      </div>

      {/* Review nudge: when corrections are waiting, say so plainly and point
          to where you act on them. This tab is read-only observability — the
          Apply / Reject controls live in an agent's Skills view — so people
          who look here for a button don't find one. Surface both facts. */}
      {(() => {
        const pending = (data.headline_metrics || [])
          .find((m) => /pending|review|awaiting/i.test(m.label));
        const n = pending ? Number(pending.value) || 0 : 0;
        if (n <= 0) return null;
        return (
          <div data-testid="cl-obs-review-nudge" style={{
            ..._card(), marginBottom: 8, borderLeft: `3px solid ${AMBER}`,
            background: `${AMBER}14`,
          }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4, color: HEADING }}>
              {n} correction{n === 1 ? "" : "s"} waiting for review
            </div>
            <div style={{ fontSize: 12.5, color: SLATE, lineHeight: 1.5 }}>
              These are draft corrections captured from failed answers. This tab
              is read-only — open the agent's <strong>Skills view</strong> to
              Apply the ones you trust (or Reject them). An applied correction
              takes effect on the agent's next query.
            </div>
          </div>
        );
      })()}

      {/* B57 (2026-07-23): explicit empty state. An empty tenant used to
          render structurally-correct tables full of 0s / "—", which is
          indistinguishable from a broken data feed — users filed "all
          metrics display 0" bugs against accurate emptiness. When the
          learning loop has produced NOTHING for this tenant, say so and
          say how data arrives. */}
      {(() => {
        const totallyEmpty =
          (data.queue_totals || []).length === 0 &&
          (data.per_agent_counts || []).length === 0 &&
          (data.per_skill_counts || []).length === 0;
        if (!totallyEmpty) return null;
        return (
          <div data-testid="cl-obs-empty-state" style={{
            ..._card(), marginBottom: 8, borderLeft: "3px solid #0d7d8c",
          }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>
              No learning activity yet — this is empty, not broken
            </div>
            <div style={{ fontSize: 12.5, color: SLATE, lineHeight: 1.5 }}>
              These metrics populate once the continual-learning loop produces
              corrections: an eval run with failures, or a production
              fact-check flagging an inaccurate answer, generates corrective
              examples for the skills attached to your agents. Until then,
              zeros here are accurate. Attach skills under the Discover tab
              and run your agents — data appears automatically.
            </div>
          </div>
        );
      })()}

      {/* 1. Headline metrics */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 4 }}>
        {data.headline_metrics.map((m) => (
          <MetricCard key={m.label} label={m.label} value={m.value} unit={m.unit} />
        ))}
      </div>
      {/* B57: the three activity cards are WINDOWED — zeros are normal for
          an idle tenant even when the tables below carry data. One line of
          context prevents the "all zeros = broken" misread. */}
      <div style={{ fontSize: 11, color: SLATE, marginBottom: 8 }}>
        "Applied / Trickle-in last 24h" and the auto-apply rate reflect recent
        activity only — zeros are normal when no eval or production correction
        ran in the window. Lifetime totals are in the tables below.
      </div>

      {/* 2. Queue depth */}
      <div style={_sectionHead}>Queue depth by source × status</div>
      <QueueBars rows={data.queue_totals} />

      {/* 3. Daily throughput */}
      <div style={_sectionHead}>Daily throughput (applied vs rejected)</div>
      <DailyLineChart series={data.per_day_series} />

      {/* 4. Per-agent */}
      <div style={_sectionHead}>Correction volume by agent</div>
      <SortableTable
        rows={data.per_agent_counts}
        empty="No agents with corrections yet."
        columns={[
          { key: "agent_name", label: "Agent" },
          { key: "instances", label: "Instances", numeric: true },
          { key: "corrections_active", label: "Active", numeric: true },
          { key: "corrections_lifetime", label: "Lifetime", numeric: true },
        ]}
      />

      {/* 5. Per-skill */}
      <div style={_sectionHead}>Correction volume by skill</div>
      <SortableTable
        rows={data.per_skill_counts}
        empty="No skills with corrections yet."
        columns={[
          { key: "base_name", label: "Skill", mono: true },
          { key: "instances", label: "Instances", numeric: true },
          { key: "corrections_active", label: "Active corrections", numeric: true },
          { key: "corrections_lifetime", label: "Lifetime", numeric: true },
        ]}
      />

      {/* 6. Recent applications */}
      <div style={_sectionHead}>Recent applications (last 20)</div>
      <SortableTable
        rows={data.recent_applications}
        empty="No applications yet."
        columns={[
          { key: "applied_at", label: "Applied at",
            render: (v) => v ? new Date(v).toLocaleString() : "—" },
          { key: "agent_name", label: "Agent" },
          { key: "base_name", label: "Skill", mono: true },
          { key: "source_channel", label: "Source",
            render: (v) => (
              <span style={{
                display: "inline-block",
                padding: "2px 6px",
                borderRadius: 3,
                fontSize: 10,
                fontWeight: 600,
                background: (CHANNEL_COLOR[v] || SLATE) + "20",
                color: CHANNEL_COLOR[v] || SLATE,
              }}>{v}</span>
            )
          },
          { key: "source_failure_id", label: "Failure ID", mono: true },
          { key: "confidence", label: "Conf", numeric: true,
            render: (v) => v == null ? "—" : v.toFixed(2) },
        ]}
      />
    </div>
  );
}
